/** Whether Sentry Session Replay may record, and how much of it.
 *
 *  Session Replay films the page: what was on screen, what was clicked, in
 *  what order. That is a recording of a PERSON, not a measurement of a page,
 *  so two separate questions gate it and both must answer yes —
 *
 *    1. Did this visitor accept analytics?  (consent, below)
 *    2. Is this visit one of the sampled few?  (the share, below)
 *
 *  Consent is the SAME choice that gates Google Analytics: the banner in
 *  `components/consent/ConsentBanner`, stored by `lib/analytics/consent`.
 *  There is deliberately no second consent mechanism to keep in step. */

import { readConsent } from '@/lib/analytics/consent'

/** THE NUMBER TO CHANGE — the share of ORDINARY visits recorded, 0…1.
 *
 *  The arithmetic, so this is changed on purpose and not by feel:
 *
 *  - Sentry's free plan includes about **50 replays a month**. It is a hard
 *    wall: once spent, nothing is recorded until the month rolls over.
 *  - The site sees roughly **30 visits a day ≈ 900 a month**.
 *  - Recording every visit (share = 1) would spend all 50 in under two days
 *    and leave the remaining 28 days blind — the failure this number exists
 *    to prevent.
 *  - `0.03 × 900 ≈ 27` ordinary visits a month — about one a day, all month —
 *    and leaves ~20 of the 50 in reserve for the recordings worth the most:
 *    the visits that hit an error, which are kept whenever they happen
 *    (`ERROR_VISIT_REPLAY_SHARE` below).
 *
 *  Only visits from people who accepted analytics are eligible at all, so the
 *  real monthly count lands BELOW 27. To watch more, raise it (0.06 ≈ two a
 *  day); to stretch the allowance further, lower it. A paid Sentry plan is the
 *  other way to raise it. `replayPolicy.test.mjs` re-checks the arithmetic. */
export const ORDINARY_VISIT_REPLAY_SHARE = 0.03

/** A visit that hits an error is always worth the recording — but still only
 *  with consent. Unchanged from the error-only setup that shipped first. */
export const ERROR_VISIT_REPLAY_SHARE = 1

/** The consent gate, covering BOTH kinds of recording (see the ADR:
 *  `docs/adr-session-replay-consent.md`). An error recording is a film of the
 *  person's last minute on the site just as an ordinary one is — the reason we
 *  want it differs, what it captures does not — and `/privacy` already promises
 *  a visitor who declines "no analytics scripts, cookies, or other
 *  non-essential storage". Replay writes storage, so the promise decides it. */
export function isReplayConsented(): boolean {
  return readConsent() === 'granted'
}
